import { withApiBaseUrl } from '../lib/apiBaseUrl'

export type EffectiveDepositMode = 'payment_gateway' | 'slip_verification' | 'manual'

export interface DepositFlowConfig {
  tenantId: string
  tenantName?: string
  paymentServiceSelected: boolean
  paymentGatewayEnabled: boolean
  slipServiceSelected: boolean
  slipVerificationEnabled: boolean
  effectiveDepositMode: EffectiveDepositMode
  depositAutoCreditEnabled: boolean
  depositAutoCreditViaPayment: boolean
  depositAutoCreditViaSlip: boolean
  depositAutoCreditViaSms: boolean
  automationSettingsWarning?: string | null
}

const DEFAULT_DEPOSIT_FLOW: DepositFlowConfig = {
  tenantId: '',
  tenantName: '',
  paymentServiceSelected: false,
  paymentGatewayEnabled: false,
  slipServiceSelected: false,
  slipVerificationEnabled: false,
  effectiveDepositMode: 'manual',
  depositAutoCreditEnabled: false,
  depositAutoCreditViaPayment: false,
  depositAutoCreditViaSlip: false,
  depositAutoCreditViaSms: false,
  automationSettingsWarning: null,
}

interface DepositFlowResponse {
  success?: boolean
  data?: Partial<DepositFlowConfig>
}

interface SlipVerificationConfigResponse {
  success?: boolean
  data?: {
    config?: {
      enabled?: boolean
      providerEnabled?: boolean
    }
    serviceState?: Partial<DepositFlowConfig>
  }
}

function normalizeDepositFlowConfig(tenantId: string, data?: Partial<DepositFlowConfig> | null): DepositFlowConfig {
  return {
    ...DEFAULT_DEPOSIT_FLOW,
    ...data,
    tenantId: data?.tenantId || tenantId,
    tenantName: data?.tenantName || tenantId,
    paymentServiceSelected: Boolean(data?.paymentServiceSelected),
    paymentGatewayEnabled: Boolean(data?.paymentGatewayEnabled),
    slipServiceSelected: Boolean(data?.slipServiceSelected),
    slipVerificationEnabled: Boolean(data?.slipVerificationEnabled),
    effectiveDepositMode: data?.effectiveDepositMode || 'manual',
    depositAutoCreditEnabled: data?.depositAutoCreditEnabled !== false,
    depositAutoCreditViaPayment: data?.depositAutoCreditViaPayment !== false,
    depositAutoCreditViaSlip: data?.depositAutoCreditViaSlip !== false,
    depositAutoCreditViaSms: data?.depositAutoCreditViaSms !== false,
    automationSettingsWarning: data?.automationSettingsWarning || null,
  }
}

function buildHeaders(tenantId: string): Record<string, string> {
  let token = ''
  try {
    token =
      localStorage.getItem('accessToken')
      || localStorage.getItem('token')
      || sessionStorage.getItem('token')
      || JSON.parse(localStorage.getItem('auth-storage') || '{}')?.state?.token
      || ''
  } catch {
    token = localStorage.getItem('accessToken') || localStorage.getItem('token') || sessionStorage.getItem('token') || ''
  }

  return {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
    ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
    ...(typeof window !== 'undefined' && window.location.hostname
      ? { 'X-Tenant-Domain': window.location.hostname }
      : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

function normalizeFallbackDepositFlowConfig(
  tenantId: string,
  payload?: SlipVerificationConfigResponse | null
): DepositFlowConfig {
  const serviceState = payload?.data?.serviceState || {}
  const config = payload?.data?.config || {}
  const slipVerificationEnabled = Boolean(
    serviceState.slipVerificationEnabled
    ?? config.enabled
    ?? config.providerEnabled
  )

  return normalizeDepositFlowConfig(tenantId, {
    tenantId,
    ...serviceState,
    slipServiceSelected: Boolean(
      serviceState.slipServiceSelected
      ?? config.enabled
      ?? config.providerEnabled
    ),
    slipVerificationEnabled,
    effectiveDepositMode: serviceState.effectiveDepositMode || (slipVerificationEnabled ? 'slip_verification' : 'manual'),
  })
}

export async function getDepositFlowConfig(tenantId: string): Promise<DepositFlowConfig> {
  try {
    const headers = buildHeaders(tenantId)
    const publicRouteUrl = withApiBaseUrl(`/api/v1/tenant-management/public/deposit-flow/${encodeURIComponent(tenantId)}?_t=${Date.now()}`)
    const depositFlowRes = await fetch(publicRouteUrl, {
      method: 'GET',
      headers,
      cache: 'no-store',
    })

    if (depositFlowRes.ok) {
      const depositFlowJson = (await depositFlowRes.json().catch(() => ({}))) as DepositFlowResponse
      return normalizeDepositFlowConfig(tenantId, {
        tenantId,
        ...(depositFlowJson?.data || {}),
      })
    }

    const fallbackRes = await fetch(
      withApiBaseUrl(`/api/v1/tenant-management/slip-verification-config?_t=${Date.now()}`),
      {
        method: 'GET',
        headers,
        cache: 'no-store',
      }
    )

    if (!fallbackRes.ok) {
      throw new Error(`Deposit flow request failed: HTTP ${depositFlowRes.status}, fallback HTTP ${fallbackRes.status}`)
    }

    const fallbackJson = (await fallbackRes.json().catch(() => ({}))) as SlipVerificationConfigResponse
    return normalizeFallbackDepositFlowConfig(tenantId, fallbackJson)
  } catch (error) {
    console.error('Error fetching deposit flow config:', error)
    return normalizeDepositFlowConfig(tenantId)
  }
}
